import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import { apiService } from "../../../services/api";
import { SaldoItemMaster } from "../../../types/empenhos";

interface ItemGerarForm {
    id_produto: number;
    id_produto_oportunidade?: number;
    produto_nome: string;
    unidade_medida?: string;
    quantidade_planejada: number;
    quantidade_gerada: number;
    quantidade_disponivel: number;
    valor_unitario: number;
    quantidade_gerar: number;
    selecionado: boolean;
}

interface ModalGerarEmpenhoProps {
    show: boolean;
    onHide: () => void;
    masterEmpenhoId: number;
    masterCodigo: string;
    onSave: () => void;
}

const ModalGerarEmpenho: React.FC<ModalGerarEmpenhoProps> = ({
    show,
    onHide,
    masterEmpenhoId,
    masterCodigo,
    onSave
}) => {
    const [itens, setItens] = useState<ItemGerarForm[]>([]);
    const [codigoEmpenho, setCodigoEmpenho] = useState("");
    const [dataEmpenho, setDataEmpenho] = useState<Date>(new Date());
    const [descricao, setDescricao] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const formatCurrency = (value: number): string => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    useEffect(() => {
        if (show && masterEmpenhoId) {
            carregarSaldo();
        }
    }, [show, masterEmpenhoId]);

    const carregarSaldo = async () => {
        setIsLoading(true);
        try {
            const response = await apiService.getSaldoMaster(masterEmpenhoId);
            if (response.success && response.data) {
                const itensForm: ItemGerarForm[] = response.data.map((saldo: SaldoItemMaster) => ({
                    id_produto: saldo.id_produto,
                    id_produto_oportunidade: saldo.id_produto_oportunidade,
                    produto_nome: saldo.produto_nome,
                    unidade_medida: saldo.unidade_medida,
                    quantidade_planejada: Number(saldo.quantidade_planejada),
                    quantidade_gerada: Number(saldo.quantidade_gerada),
                    quantidade_disponivel: Number(saldo.quantidade_disponivel),
                    valor_unitario: Number(saldo.valor_unitario),
                    quantidade_gerar: 0,
                    selecionado: false
                }));
                setItens(itensForm);
            }
        } catch (error: any) {
            toast.error(error.message || "Erro ao carregar saldo do empenho mestre");
        } finally {
            setIsLoading(false);
        }
    };

    const handleQuantidadeChange = (index: number, value: string) => {
        const quantidade = parseFloat(value) || 0;
        setItens(prev => {
            const novosItens = [...prev];
            const item = { ...novosItens[index] };

            if (quantidade > item.quantidade_disponivel) {
                toast.warning(`Quantidade máxima disponível: ${item.quantidade_disponivel}`);
                item.quantidade_gerar = item.quantidade_disponivel;
            } else {
                item.quantidade_gerar = quantidade;
            }

            item.selecionado = item.quantidade_gerar > 0;
            novosItens[index] = item;
            return novosItens;
        });
    };

    const handleSelecionarTodos = () => {
        setItens(prev => prev.map(item => ({
            ...item,
            quantidade_gerar: item.quantidade_disponivel > 0 ? item.quantidade_disponivel : 0,
            selecionado: item.quantidade_disponivel > 0
        })));
    };

    const calcularValorTotal = (): number => {
        return itens.reduce((total, item) => {
            if (item.selecionado && item.quantidade_gerar > 0) {
                return total + (item.quantidade_gerar * item.valor_unitario);
            }
            return total;
        }, 0);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const itensSelecionados = itens.filter(i => i.selecionado && i.quantidade_gerar > 0);

            if (itensSelecionados.length === 0) {
                toast.error("Selecione pelo menos um item com quantidade para gerar.");
                setIsSubmitting(false);
                return;
            }

            const dados = {
                id_empenho_pai: masterEmpenhoId,
                codigo_empenho: codigoEmpenho || undefined,
                data_empenho: dataEmpenho.toISOString().split('T')[0],
                descricao: descricao || undefined,
                itens: itensSelecionados.map(item => ({
                    id_produto: item.id_produto,
                    id_produto_oportunidade: item.id_produto_oportunidade,
                    quantidade: item.quantidade_gerar,
                    valor_unitario: item.valor_unitario
                }))
            };

            const response = await apiService.gerarFilhoEmpenho(dados);
            if (response.success) {
                toast.success(response.message || "Empenho gerado com sucesso!");
                onSave();
            } else {
                toast.error(response.message || "Erro ao gerar empenho.");
            }
        } catch (error: any) {
            toast.error(error.message || "Erro ao gerar empenho filho.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            centered
            className="custom-modal"
            backdrop="static"
            keyboard={!isSubmitting}
        >
            <Modal.Header closeButton={!isSubmitting}>
                <Modal.Title>
                    Gerar Autorização a partir de {masterCodigo}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoading ? (
                    <div className="text-center p-4">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Carregando...</span>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        {/* Codigo e Data */}
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="col-form-label">Codigo do Empenho</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={codigoEmpenho}
                                    onChange={(e) => setCodigoEmpenho(e.target.value)}
                                    placeholder="Gerado automaticamente se vazio"
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="col-form-label">
                                    Data do Empenho <span className="text-danger">*</span>
                                </label>
                                <DatePicker
                                    selected={dataEmpenho}
                                    onChange={(date) => date && setDataEmpenho(date)}
                                    className="form-control"
                                    dateFormat="dd/MM/yyyy"
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        {/* Descricao */}
                        <div className="mb-3">
                            <label className="col-form-label">Descricao</label>
                            <textarea
                                className="form-control"
                                rows={2}
                                value={descricao}
                                onChange={(e) => setDescricao(e.target.value)}
                                disabled={isSubmitting}
                            />
                        </div>

                        {/* Tabela de Itens */}
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="col-form-label fw-semibold mb-0">
                                    Itens para Gerar
                                </label>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={handleSelecionarTodos}
                                    disabled={isSubmitting}
                                >
                                    <i className="ti ti-checks me-1"></i>
                                    Selecionar Todo Disponivel
                                </button>
                            </div>

                            <div className="table-responsive">
                                <table className="table table-sm table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Produto</th>
                                            <th className="text-center" style={{ width: '100px' }}>Planejado</th>
                                            <th className="text-center" style={{ width: '100px' }}>Ja Gerado</th>
                                            <th className="text-center" style={{ width: '100px' }}>Disponivel</th>
                                            <th className="text-center" style={{ width: '120px' }}>Qtd a Gerar</th>
                                            <th className="text-end" style={{ width: '120px' }}>Subtotal</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {itens.map((item, index) => {
                                            const disponivel = item.quantidade_disponivel > 0;
                                            const percentualGerado = item.quantidade_planejada > 0
                                                ? (item.quantidade_gerada / item.quantidade_planejada) * 100
                                                : 0;

                                            return (
                                                <tr key={item.id_produto} className={!disponivel ? 'table-secondary' : ''}>
                                                    <td>
                                                        <div>
                                                            <span className="fw-medium">{item.produto_nome}</span>
                                                            <br />
                                                            <small className="text-muted">
                                                                {formatCurrency(item.valor_unitario)} / {item.unidade_medida || 'un'}
                                                            </small>
                                                        </div>
                                                    </td>
                                                    <td className="text-center align-middle">
                                                        {Number(item.quantidade_planejada).toLocaleString('pt-BR')}
                                                    </td>
                                                    <td className="text-center align-middle">
                                                        <div>
                                                            {Number(item.quantidade_gerada).toLocaleString('pt-BR')}
                                                            <br />
                                                            <small className={`badge ${percentualGerado >= 100 ? 'bg-success' : percentualGerado > 0 ? 'bg-warning' : 'bg-secondary'}`}>
                                                                {percentualGerado.toFixed(0)}%
                                                            </small>
                                                        </div>
                                                    </td>
                                                    <td className="text-center align-middle fw-bold">
                                                        {Number(item.quantidade_disponivel).toLocaleString('pt-BR')}
                                                    </td>
                                                    <td className="text-center align-middle">
                                                        {disponivel ? (
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm text-center"
                                                                value={item.quantidade_gerar || ""}
                                                                onChange={(e) => handleQuantidadeChange(index, e.target.value)}
                                                                step="0.001"
                                                                min="0"
                                                                max={item.quantidade_disponivel}
                                                                placeholder="0"
                                                                disabled={isSubmitting}
                                                            />
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                    <td className="text-end align-middle">
                                                        {item.quantidade_gerar > 0
                                                            ? formatCurrency(item.quantidade_gerar * item.valor_unitario)
                                                            : '-'
                                                        }
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Valor Total */}
                        <div className="d-flex justify-content-end mb-3">
                            <h5>Valor Total: {formatCurrency(calcularValorTotal())}</h5>
                        </div>

                        {/* Botoes */}
                        <div className="col-lg-12 text-end modal-btn mt-4 pt-3 border-top">
                            <button
                                type="button"
                                className="btn btn-light me-2"
                                onClick={onHide}
                                disabled={isSubmitting}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={isSubmitting || itens.filter(i => i.selecionado && i.quantidade_gerar > 0).length === 0}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Gerando...
                                    </>
                                ) : (
                                    <>
                                        <i className="ti ti-send me-1"></i>
                                        Gerar Empenho
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default ModalGerarEmpenho;
